<script setup>
import { computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { isImmutableSchemaKnowledgebase } from './composables/useImmutableSchemaKnowledgebase';

const props = defineProps({
  knowledgebaseId: {
    type: String,
    required: true,
  },
});

const route = useRoute();
const store = useStore();
const { t } = useI18n();

const selectedKnowledgebase = computed(
  () => store.getters['knowledgebase/getSelectedLibrary']
);
const pagination = computed(() => store.getters['records/getPagination']);
const sitemaps = computed(() => store.getters['sitemaps/getSites']);
const recordsUIFlags = computed(() => store.getters['records/getUIFlags']);
const sitemapsUIFlags = computed(() => store.getters['sitemaps/getUIFlags']);

const routeParams = computed(() => ({
  accountId: route.params.accountId,
  knowledgebaseId: props.knowledgebaseId,
}));

const recordsCountText = computed(() => {
  if (recordsUIFlags.value.isFetching) return t('LIBRARIES.TOOLS.LOADING');
  return t('LIBRARIES.TOOLS.RECORDS_COUNT', {
    count: pagination.value?.totalCount ?? 0,
  });
});

const sitemapsCountText = computed(() => {
  if (sitemapsUIFlags.value.isFetching) return t('LIBRARIES.TOOLS.LOADING');
  const count = sitemaps.value?.length ?? 0;
  return t('LIBRARIES.TOOLS.SITEMAPS_COUNT', { count });
});

const contentRoute = computed(() => ({
  name: 'settings_knowledgebases_content',
  params: routeParams.value,
}));

const showSchemaTool = computed(
  () => !isImmutableSchemaKnowledgebase(selectedKnowledgebase.value)
);

function ensureKnowledgebaseLoaded() {
  const current = selectedKnowledgebase.value;
  if (!current || String(current.id) !== String(props.knowledgebaseId)) {
    store.dispatch('knowledgebase/getSingleLibrary', props.knowledgebaseId);
  }
}

function loadData() {
  store.dispatch('records/get', props.knowledgebaseId);
  store.dispatch('sitemaps/get', props.knowledgebaseId);
}

async function handleResync(site) {
  await store.dispatch('sitemaps/reindex', {
    knowledgebaseId: props.knowledgebaseId,
    siteId: site.id,
  });
  loadData();
}

async function handleDelete(site) {
  if (!window.confirm(t('LIBRARIES.TOOLS.DELETE_SITEMAP_CONFIRM'))) return;
  await store.dispatch('sitemaps/delete', {
    knowledgebaseId: props.knowledgebaseId,
    siteId: site.id,
  });
  loadData();
}

onMounted(() => {
  ensureKnowledgebaseLoaded();
  loadData();
});

watch(
  () => props.knowledgebaseId,
  () => {
    ensureKnowledgebaseLoaded();
    loadData();
  }
);
</script>

<template>
  <div class="flex-1 overflow-auto h-full pt-6 p-8">
    <h1
      v-if="
        selectedKnowledgebase &&
        String(selectedKnowledgebase.id) === knowledgebaseId
      "
      class="text-2xl font-semibold text-neutral-900 mb-1"
    >
      {{ selectedKnowledgebase.name }} - {{ $t('LIBRARIES.TOOLS.TITLE') }}
    </h1>

    <div class="flex flex-wrap gap-4 mb-6 text-sm text-neutral-600">
      <span>{{ recordsCountText }}</span>
      <span>{{ sitemapsCountText }}</span>
    </div>

    <div class="mb-6 p-4 border border-woot-200 rounded-lg bg-white">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-lg font-semibold text-neutral-900">
          {{ $t('LIBRARIES.TOOLS.SITEMAPS') }}
        </h2>
        <router-link
          :to="contentRoute"
          class="text-woot-600 hover:text-woot-700 font-medium"
        >
          {{ $t('LIBRARIES.TOOLS.VIEW_DOCUMENTS') }}
        </router-link>
      </div>
      <div v-if="sitemapsUIFlags.isFetching" class="text-neutral-500 py-2">
        {{ $t('LIBRARIES.TOOLS.LOADING') }}
      </div>
      <div
        v-else-if="!sitemaps || sitemaps.length === 0"
        class="text-neutral-500 py-2"
      >
        {{ $t('LIBRARIES.TOOLS.NO_SITEMAPS') }}
      </div>
      <table v-else class="w-full woot-table">
        <thead class="border-b border-neutral-100">
          <tr>
            <th class="text-left">{{ $t('LIBRARIES.TOOLS.URL') }}</th>
            <th class="text-left w-28">
              {{ $t('LIBRARIES.RECORDS.STATUS.LABEL') }}
            </th>
            <th
              colspan="2"
              class="text-left text-neutral-500 font-normal text-sm"
            >
              {{ $t('LIBRARIES.TOOLS.CONFIG') }}
            </th>
            <th class="w-24 text-right" />
          </tr>
          <tr class="border-b border-neutral-100">
            <th />
            <th />
            <th class="text-left text-neutral-500 font-normal text-sm w-100">
              {{ $t('LIBRARIES.TOOLS.REGEX') }}
            </th>
            <th class="text-left text-neutral-500 font-normal text-sm w-28">
              {{ $t('LIBRARIES.TOOLS.AUTOUPDATE') }}
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="site in sitemaps"
            :key="site.id"
            class="border-b border-neutral-100"
          >
            <td>
              <span class="font-mono text-sm break-all text-neutral-800">
                {{ site.url }}
              </span>
            </td>
            <td>
              <span
                class="px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-700"
              >
                {{ site.status }}
              </span>
            </td>
            <td
              class="text-sm text-neutral-500 max-w-[12rem] truncate"
              :title="site.regex || ''"
            >
              {{ site.regex || '—' }}
            </td>
            <td class="text-sm text-neutral-500">
              {{
                site.autoupdate
                  ? $t('LIBRARIES.TOOLS.AUTOUPDATE_ON')
                  : $t('LIBRARIES.TOOLS.AUTOUPDATE_OFF')
              }}
            </td>
            <td class="text-right">
              <NextButton
                v-tooltip.top-end="$t('LIBRARIES.TOOLS.RESYNC')"
                icon="i-lucide-refresh-ccw-dot"
                md
                slate
                :is-loading="sitemapsUIFlags.isReindexing"
                @click="handleResync(site)"
              />
              <NextButton
                v-tooltip.top-end="$t('LIBRARIES.TOOLS.DELETE')"
                icon="i-lucide-trash-2"
                md
                slate
                @click="handleDelete(site)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-2">
      <router-link
        :to="{ name: 'settings_knowledgebases_search', params: routeParams }"
        class="p-4 border border-2 rounded-lg border-woot-200 hover:border-woot-600 transition-colors"
      >
        <div class="flex items-center">
          <i class="i-lucide-search text-2xl mr-2 text-woot-400" />
          <h2 class="text-lg font-semibold">Search</h2>
        </div>
        <p class="text-gray-600 mt-2 pl-8">
          Test and debug your knowledge base search functionality
        </p>
      </router-link>

      <router-link
        v-if="showSchemaTool"
        :to="{ name: 'settings_knowledgebases_schema', params: routeParams }"
        class="p-4 border border-2 rounded-lg border-woot-200 hover:border-woot-600 transition-colors"
      >
        <div class="flex items-center">
          <i class="i-lucide-braces text-2xl mr-2 text-woot-400" />
          <h2 class="text-lg font-semibold">Schema</h2>
        </div>
        <p class="text-gray-600 mt-2 pl-8">
          View and manage your knowledge base schema configuration
        </p>
      </router-link>

      <router-link
        :to="{ name: 'settings_knowledgebases_indexing', params: routeParams }"
        class="p-4 border border-2 rounded-lg border-woot-200 hover:border-woot-600 transition-colors"
      >
        <div class="flex items-center">
          <i class="i-lucide-grid-2x2-check text-2xl mr-2 text-woot-400" />
          <h2 class="text-lg font-semibold">Indexing</h2>
        </div>
        <p class="text-gray-600 mt-2 pl-8">
          See the indexing status of document and trigger a reindex
        </p>
      </router-link>

      <router-link
        :to="{ name: 'settings_knowledgebases_eval', params: routeParams }"
        class="p-4 border border-2 rounded-lg border-woot-200 hover:border-woot-600 transition-colors"
      >
        <div class="flex items-center">
          <i class="i-lucide-flask-conical text-2xl mr-2 text-woot-400" />
          <h2 class="text-lg font-semibold">Eval</h2>
        </div>
        <p class="text-gray-600 mt-2 pl-8">
          {{ $t('LIBRARIES.EVAL.TOOLS_DESCRIPTION') }}
        </p>
      </router-link>

      <router-link
        :to="{ name: 'settings_knowledgebases_nearby', params: routeParams }"
        class="p-4 border border-2 rounded-lg border-woot-200 hover:border-woot-600 transition-colors"
      >
        <div class="flex items-center gap-2">
          <i class="i-lucide-map-pin text-2xl mr-2 text-woot-400" />
          <h2 class="text-lg font-semibold">
            {{ $t('LIBRARIES.NEARBY.TITLE') }}
          </h2>
          <span
            class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800"
          >
            {{ $t('LIBRARIES.NEARBY.BETA') }}
          </span>
        </div>
        <p class="text-gray-600 mt-2 pl-8">
          {{ $t('LIBRARIES.NEARBY.DESCRIPTION') }}
        </p>
      </router-link>

      <router-link
        :to="{ name: 'settings_knowledgebases_itinerary', params: routeParams }"
        class="p-4 border border-2 rounded-lg border-woot-200 hover:border-woot-600 transition-colors"
      >
        <div class="flex items-center gap-2">
          <i class="i-lucide-route text-2xl mr-2 text-woot-400" />
          <h2 class="text-lg font-semibold">
            {{ $t('LIBRARIES.ITINERARY.TITLE') }}
          </h2>
          <span
            class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800"
          >
            {{ $t('LIBRARIES.ITINERARY.BETA') }}
          </span>
        </div>
        <p class="text-gray-600 mt-2 pl-8">
          {{ $t('LIBRARIES.ITINERARY.DESCRIPTION') }}
        </p>
      </router-link>

      <router-link
        :to="{ name: 'settings_knowledgebases_records', params: routeParams }"
        class="p-4 border border-2 rounded-lg border-woot-200 hover:border-woot-600 transition-colors"
      >
        <div class="flex items-center gap-2">
          <i class="i-lucide-database text-2xl mr-2 text-woot-400" />
          <h2 class="text-lg font-semibold">
            {{ $t('LIBRARIES.RECORDS_TOOL.TITLE') }}
          </h2>
        </div>
        <p class="text-gray-600 mt-2 pl-8">
          {{ $t('LIBRARIES.RECORDS_TOOL.DESCRIPTION') }}
        </p>
      </router-link>
    </div>
  </div>
</template>
